/**
 * 绿色码头 (green-harbor) map data — 60 × 36 tiles, authored procedurally so the layout is
 * readable, testable and easy to retune. All rect-like fields in the resulting MapData
 * (exits, interactions, water, rainShelter, lights, spawns, npc placements) are in WORLD PIXELS;
 * this file works in TILE units and converts at the end via `px()` / `feet()`.
 *
 * Layout (tile coords, +x east, +y south; the sea is NORTH because the harbor is the northern
 * tip of 南陆):
 *
 *   y 0..8   sea, foam edge at y 8; two stilted piers (x 5..8 and x 16..19), a foreign-hulled
 *            ship moored west of pier A, a beached junk on the eastern sand (x 42..50)
 *   y 9      boardwalk planks along the shore
 *   y 10     shore sand (pier commentary beat fires here)
 *   y 11..15 cargo yard: stone paving, warehouse shed, two cranes, crate stacks, jars, barrels
 *   y 16     grass band with the earth road (x 12..15) down into the market
 *   y 17..28 market plaza under huge leaves / palms: candle, spice, cloth, strange-fruit, fish
 *            stalls, a big 亚北 jar stall and the 亚北青年's stall under a palm (x 36..39)
 *   y 23..33 大海龟旅店 on the far (east) side: shell roof, columns, two small turtles at the door
 *   y 29..35 southern grass, stilted huts, palms
 *
 * Foreground props are anchored by the runtime at origin (0.5, 1): `x` is the horizontal centre of
 * the footprint, `y` its bottom edge. Texture sizes live in PROP_SPECS (dev textures are generated
 * to those sizes; real art must match them, see docs/asset-requests/harbor-builder.md).
 */
import type { Facing } from '@/core/types';
import type { MapData, MapInteraction, MapLightData, MapNpcPlacement } from '@/core/types/contracts';
import { GREEN_HARBOR_NPCS } from '../data/npcs';
import { TILE, feet, px, rectPx, span, type TileRect } from './coords';

export { TILE, feet, px, rectPx, span, type TileRect } from './coords';

export const GH_MAP_ID = 'green-harbor';
export const GH_W = 60;
export const GH_H = 36;

/** Tile ids of the `gh-tiles` tileset (left→right, top→bottom, 6 columns). -1 = empty cell. */
export const T = {
  WATER: 0,
  WATER_DEEP: 1,
  SAND: 2,
  EARTH: 3,
  STONE: 4,
  PLANK: 5,
  PLANK_EDGE: 6,
  GRASS: 7,
  PAVING: 8,
  SHADOW: 9,
  LEAF: 10,
  LEAF_EDGE: 11,
  WET_SAND: 12,
  INN_FLOOR: 13,
  FOAM: 14,
  PALM_SHADOW: 15,
  LEAF_SHADE: 16,
  ROAD_EDGE: 17,
} as const;
export const EMPTY = -1;
export const GH_TILESET_KEY = 'gh-tiles';
export const GH_TILESET_COLUMNS = 6;
export const GH_TILE_COUNT = 96;

/** Prop texture keys (dev placeholders are generated at runtime; see docs/asset-requests/harbor-builder.md). */
export const P = {
  FOREIGN_SHIP: 'gh-foreign-ship',
  JUNK: 'gh-junk',
  SHED: 'gh-shed',
  CRANE: 'gh-crane',
  CRATES: 'gh-crate-stack',
  BARRELS: 'gh-barrels',
  JARS: 'gh-jars',
  ROPE: 'gh-rope-coil',
  NET: 'gh-drying-net',
  STALL_CANDLE: 'gh-stall-candle',
  STALL_SPICE: 'gh-stall-spice',
  STALL_CLOTH: 'gh-stall-cloth',
  STALL_FRUIT: 'gh-stall-fruit',
  STALL_FISH: 'gh-stall-fish',
  STALL_JARS: 'gh-stall-yabei-jars',
  STALL_YABEI: 'gh-stall-yabei',
  CACTUS: 'gh-cactus-pot',
  PELT: 'gh-kangaroo-pelt',
  EGG: 'gh-ostrich-egg',
  PALM: 'gh-palm',
  LANTERN: 'gh-lantern-post',
  HUT: 'gh-stilt-hut',
  INN: 'gh-turtle-inn',
  TURTLE: 'gh-small-turtle',
  BOLLARD: 'gh-bollard',
  SIGN_INN: 'gh-inn-sign',
  COLUMN: 'gh-inn-column',
} as const;
export type PropKey = (typeof P)[keyof typeof P];

/** Texture size (px) per prop key; width = footprint width (palms/leaves overhang), height includes the vertical face. */
export const PROP_SPECS: Record<PropKey, { w: number; h: number }> = {
  [P.FOREIGN_SHIP]: { w: 160, h: 224 },
  [P.JUNK]: { w: 288, h: 192 },
  [P.SHED]: { w: 224, h: 112 },
  [P.CRANE]: { w: 64, h: 128 },
  [P.CRATES]: { w: 64, h: 72 },
  [P.BARRELS]: { w: 32, h: 40 },
  [P.JARS]: { w: 64, h: 44 },
  [P.ROPE]: { w: 32, h: 24 },
  [P.NET]: { w: 64, h: 64 },
  [P.STALL_CANDLE]: { w: 96, h: 96 },
  [P.STALL_SPICE]: { w: 96, h: 96 },
  [P.STALL_CLOTH]: { w: 96, h: 96 },
  [P.STALL_FRUIT]: { w: 96, h: 96 },
  [P.STALL_FISH]: { w: 96, h: 96 },
  [P.STALL_JARS]: { w: 128, h: 96 },
  [P.STALL_YABEI]: { w: 128, h: 96 },
  [P.CACTUS]: { w: 32, h: 32 },
  [P.PELT]: { w: 32, h: 48 },
  [P.EGG]: { w: 32, h: 28 },
  [P.PALM]: { w: 64, h: 112 },
  [P.LANTERN]: { w: 32, h: 64 },
  [P.HUT]: { w: 128, h: 136 },
  [P.INN]: { w: 384, h: 360 },
  [P.TURTLE]: { w: 32, h: 24 },
  [P.BOLLARD]: { w: 32, h: 32 },
  [P.SIGN_INN]: { w: 32, h: 48 },
  [P.COLUMN]: { w: 32, h: 88 },
};

/** Named landmarks in tile coordinates — used by the scene, timelines and tests. */
export const GH = {
  pierA: span(5, 2, 8, 9),
  pierB: span(16, 3, 19, 9),
  boardwalk: span(2, 9, 36, 9),
  shore: span(0, 10, 59, 10),
  cargoYard: span(2, 11, 30, 15),
  road: span(12, 15, 15, 17),
  plaza: span(10, 17, 46, 28),
  innApproach: span(44, 24, 59, 35),
  inn: span(47, 23, 58, 32),
  innDoor: span(51, 32, 53, 32),
  innPorch: span(48, 33, 57, 34),
  canopyA: span(24, 20, 31, 26),
  canopyB: span(34, 20, 42, 26),
  stalls: {
    candle: span(17, 18, 19, 19),
    spice: span(22, 18, 24, 19),
    cloth: span(27, 18, 29, 19),
    fruit: span(32, 18, 34, 19),
    fish: span(11, 24, 13, 25),
    jars: span(26, 23, 29, 24),
    yabei: span(36, 23, 39, 24),
  },
  merchantPalm: { x: 41, y: 21 },
  beats: {
    pier: span(2, 9, 36, 10),
    candle: span(14, 20, 21, 21),
    market: span(14, 22, 26, 24),
    leaves: span(24, 25, 33, 27),
  },
  merchantTalk: span(35, 25, 40, 26),
  /**
   * First clear view of the inn (P12 opening narration). The scene plays it right after gh.rain (canon:
   * it follows “一会儿就下雨了”); this auto rect is the fallback for a resume at RAIN_APPROACHES without
   * the mark — it starts one row below the rain-beat end tile (row 29) so a resumed player walks into it.
   */
  innFarView: span(44, 30, 46, 34),
  spawns: {
    /** J's start for the opening (Z stands three tiles further out on the pier). */
    pierStart: { x: 6, y: 7, facing: 'up' as Facing },
    market: { x: 13, y: 19, facing: 'right' as Facing },
    innApproach: { x: 44, y: 31, facing: 'right' as Facing },
    innDoor: { x: 52, y: 34, facing: 'down' as Facing },
  },
  /** Where 赵呈博 drifts to while looking at the sky (RAIN_APPROACHES beat). */
  rainWalk: { from: { x: 41, y: 27 }, to: { x: 45, y: 29 } },
  /** Where 江子潮 steps to when he shakes his head and walks away from the merchant. */
  merchantWalkAway: { x: 42, y: 27 },
} as const;

interface Builder {
  ground: number[]; detail: number[]; overhead: number[]; collision: number[];
  foreground: { key: string; x: number; y: number; depthY?: number }[];
  interactions: MapInteraction[];
  lights: MapLightData[];
  water: TileRect[]; shelter: TileRect[];
}

function idx(x: number, y: number): number { return y * GH_W + x; }
function inBounds(x: number, y: number): boolean { return x >= 0 && y >= 0 && x < GH_W && y < GH_H; }
function fill(layer: number[], r: TileRect, tile: number): void {
  for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) if (inBounds(x, y)) layer[idx(x, y)] = tile;
}
function solid(b: Builder, r: TileRect, value = 1): void { fill(b.collision, r, value); }
function openAt(b: Builder, x: number, y: number): void { if (inBounds(x, y)) b.collision[idx(x, y)] = 0; }

/**
 * A prop occupying `footprint` (solid unless `passable`). Anchored at the footprint's bottom-centre
 * (runtime origin 0.5, 1); depth = bottom edge unless `depthRow` (tile row whose top edge sorts it).
 */
function prop(b: Builder, key: PropKey, footprint: TileRect, opts: { depthRow?: number; passable?: boolean } = {}): void {
  const x = px(footprint.x) + (footprint.w * TILE) / 2;
  const y = px(footprint.y + footprint.h);
  b.foreground.push({ key, x, y, depthY: opts.depthRow !== undefined ? px(opts.depthRow) : y });
  if (!opts.passable) solid(b, footprint);
}
function stall(b: Builder, key: PropKey, r: TileRect): void {
  prop(b, key, r);
  b.shelter.push({ x: r.x, y: r.y, w: r.w, h: r.h + 1 }); // awning covers the counter and the row in front
  fill(b.detail, { x: r.x, y: r.y + r.h, w: r.w, h: 1 }, T.SHADOW);
}
function palm(b: Builder, x: number, y: number, withShadow = true): void {
  prop(b, P.PALM, { x, y, w: 1, h: 1 });
  if (withShadow) fill(b.detail, { x: x - 1, y: y - 1, w: 3, h: 2 }, T.PALM_SHADOW);
}
function lantern(b: Builder, x: number, y: number, color = '#ffb347'): void {
  prop(b, P.LANTERN, { x, y, w: 1, h: 1 });
  b.lights.push({ x: px(x) + 16, y: px(y) + 4, radius: 72, color, intensity: 0.8, flicker: true });
}
function observe(b: Builder, id: string, r: TileRect, dialogue: string, label: string, extra: Partial<MapInteraction> = {}): void {
  b.interactions.push({ id, ...rectPx(r), kind: 'observe', dialogue, label, ...extra });
}
/** Giant-leaf canopy: shade on the ground, an overhead leaf frame (top row + sides) so people underneath stay visible. */
function canopy(b: Builder, r: TileRect): void {
  fill(b.detail, r, T.SHADOW);
  fill(b.overhead, r, T.LEAF_SHADE);
  fill(b.overhead, span(r.x, r.y, r.x + r.w - 1, r.y), T.LEAF);
  fill(b.overhead, span(r.x, r.y + 1, r.x, r.y + r.h - 1), T.LEAF_EDGE);
  fill(b.overhead, span(r.x + r.w - 1, r.y + 1, r.x + r.w - 1, r.y + r.h - 1), T.LEAF_EDGE);
  b.shelter.push(r);
}

export function buildGreenHarborMap(): MapData {
  const n = GH_W * GH_H;
  const b: Builder = {
    ground: new Array<number>(n).fill(T.SAND), detail: new Array<number>(n).fill(EMPTY),
    overhead: new Array<number>(n).fill(EMPTY), collision: new Array<number>(n).fill(0),
    foreground: [], interactions: [], lights: [], water: [], shelter: [],
  };

  // ---------------------------------------------------------------- terrain
  fill(b.ground, span(0, 16, 59, 35), T.GRASS);
  fill(b.ground, span(0, 0, 59, 7), T.WATER);
  fill(b.ground, span(0, 0, 59, 2), T.WATER_DEEP);
  fill(b.ground, span(0, 8, 39, 8), T.FOAM);
  fill(b.ground, span(40, 5, 59, 9), T.SAND);                       // eastern beach bulge (the junk lies here)
  fill(b.ground, span(40, 4, 59, 4), T.FOAM);
  fill(b.ground, span(40, 8, 59, 8), T.WET_SAND);
  solid(b, span(0, 0, 59, 8));                                       // sea is solid …
  solid(b, span(40, 5, 59, 8), 0);                                   // … except the beach bulge
  b.water.push(span(0, 0, 39, 8), span(40, 0, 59, 4));
  fill(b.ground, GH.boardwalk, T.PLANK);
  fill(b.ground, GH.pierA, T.PLANK); solid(b, GH.pierA, 0);
  fill(b.ground, GH.pierB, T.PLANK); solid(b, GH.pierB, 0);
  fill(b.detail, span(5, 2, 5, 8), T.PLANK_EDGE); fill(b.detail, span(8, 2, 8, 8), T.PLANK_EDGE);
  fill(b.detail, span(16, 3, 16, 8), T.PLANK_EDGE); fill(b.detail, span(19, 3, 19, 8), T.PLANK_EDGE);
  fill(b.ground, GH.shore, T.SAND);
  fill(b.ground, GH.cargoYard, T.STONE);
  fill(b.ground, GH.road, T.EARTH);
  fill(b.detail, span(11, 16, 11, 17), T.ROAD_EDGE); fill(b.detail, span(16, 16, 16, 17), T.ROAD_EDGE);
  fill(b.ground, GH.plaza, T.PAVING);
  fill(b.ground, GH.innApproach, T.EARTH);
  fill(b.ground, span(44, 24, 46, 28), T.PAVING);                     // plaza bleeds into the inn approach
  fill(b.ground, span(47, 32, 58, 34), T.INN_FLOOR);                  // inn threshold / porch floor
  // map border is solid so nobody walks off the world
  solid(b, span(0, 0, 59, 0)); solid(b, span(0, 35, 59, 35)); solid(b, span(0, 0, 0, 35)); solid(b, span(59, 0, 59, 35));

  // ---------------------------------------------------------------- ships and piers
  prop(b, P.FOREIGN_SHIP, span(0, 1, 4, 6));                          // tall pale hull west of pier A
  prop(b, P.JUNK, span(42, 4, 50, 8));                                // beached junk on the eastern sand
  b.shelter.push(span(43, 9, 50, 9));                                 // the tilted hull overhangs the sand below it
  fill(b.detail, span(43, 9, 50, 9), T.SHADOW);
  for (const x of [4, 9, 15, 20]) prop(b, P.BOLLARD, { x, y: 9, w: 1, h: 1 });
  prop(b, P.NET, span(38, 10, 39, 10));
  prop(b, P.ROPE, span(21, 14, 21, 14));

  // ---------------------------------------------------------------- cargo yard
  prop(b, P.SHED, span(2, 11, 8, 12));
  b.shelter.push(span(2, 13, 8, 13));                                 // shed awning
  fill(b.detail, span(2, 13, 8, 13), T.SHADOW);
  prop(b, P.CRANE, span(10, 11, 11, 12));
  prop(b, P.CRANE, span(22, 11, 23, 12));
  prop(b, P.CRATES, span(14, 12, 15, 13));
  prop(b, P.CRATES, span(26, 12, 27, 13));
  prop(b, P.CRATES, span(4, 14, 5, 14));
  prop(b, P.BARRELS, span(8, 14, 8, 14));
  prop(b, P.JARS, span(18, 14, 19, 14));
  prop(b, P.CRATES, span(29, 15, 30, 15));

  // ---------------------------------------------------------------- market
  canopy(b, GH.canopyA); canopy(b, GH.canopyB);
  stall(b, P.STALL_CANDLE, GH.stalls.candle);
  stall(b, P.STALL_SPICE, GH.stalls.spice);
  stall(b, P.STALL_CLOTH, GH.stalls.cloth);
  stall(b, P.STALL_FRUIT, GH.stalls.fruit);
  stall(b, P.STALL_FISH, GH.stalls.fish);
  stall(b, P.STALL_JARS, GH.stalls.jars);
  stall(b, P.STALL_YABEI, GH.stalls.yabei);
  prop(b, P.CACTUS, span(35, 23, 35, 23));
  prop(b, P.EGG, span(40, 23, 40, 23));
  palm(b, GH.merchantPalm.x, GH.merchantPalm.y, false);
  prop(b, P.PELT, span(41, 22, 41, 22));                              // pelt hangs on the merchant's palm
  for (const [x, y] of [[3, 17], [9, 20], [22, 22], [46, 17], [47, 21], [20, 29], [40, 30], [58, 20], [30, 29], [6, 27], [53, 20]]) palm(b, x as number, y as number);
  lantern(b, 16, 20); lantern(b, 31, 20); lantern(b, 35, 27); lantern(b, 25, 27); lantern(b, 12, 27);

  // ---------------------------------------------------------------- 大海龟旅店
  // Body (shell roof + dark interior) sorts behind anyone on the porch; columns and turtles sort by their own feet.
  prop(b, P.INN, GH.inn);
  fill(b.collision, GH.innDoor, 0);                                   // doorway between the columns
  for (const x of [47, 50, 54, 58]) prop(b, P.COLUMN, { x, y: 33, w: 1, h: 1 });
  prop(b, P.TURTLE, span(49, 33, 49, 33)); prop(b, P.TURTLE, span(55, 33, 55, 33));
  prop(b, P.SIGN_INN, span(56, 34, 56, 34));
  openAt(b, 52, 33);
  b.shelter.push(GH.innPorch);
  fill(b.detail, GH.innPorch, T.SHADOW);
  lantern(b, 46, 33, '#ffcf70'); lantern(b, 58, 34, '#ffcf70');
  b.lights.push({ x: px(52) + 16, y: px(32) + 16, radius: 120, color: '#ffb347', intensity: 0.9, flicker: true });

  // ---------------------------------------------------------------- southern huts
  prop(b, P.HUT, span(12, 30, 15, 32));
  prop(b, P.HUT, span(22, 30, 25, 32));
  prop(b, P.HUT, span(32, 30, 35, 32));

  // ---------------------------------------------------------------- observation points (flavour only)
  observe(b, 'obs-foreign-ship', span(5, 2, 5, 6), 'gh.obs.foreign-ship', '看看那艘船');
  observe(b, 'obs-pier-end', span(6, 2, 8, 2), 'gh.obs.pier-end', '眺望海面');
  observe(b, 'obs-junk', span(41, 9, 50, 9), 'gh.obs.junk', '看看搁浅的帆船');
  observe(b, 'obs-net', span(37, 11, 40, 11), 'gh.obs.net', '看看渔网');
  observe(b, 'obs-crane', span(9, 13, 12, 13), 'gh.obs.crane', '看看起重架');
  observe(b, 'obs-crane-2', span(21, 13, 24, 13), 'gh.obs.crane', '看看起重架');
  observe(b, 'obs-crates', span(13, 14, 16, 14), 'gh.obs.crates', '看看货箱');
  observe(b, 'obs-jars', span(17, 15, 20, 15), 'gh.obs.jars', '看看陶罐');
  observe(b, 'obs-shed', span(2, 13, 8, 13), 'gh.obs.shed', '看看货棚');
  // Vendor stalls: the row in front of the counter is one `talk` prompt — the goods observation, then the
  // vendor behind the counter answers (data/npcs.ts `stall`, data/dialogues.ts `gh.stall.<short>`). The
  // counter is two rows deep, so the vendors are out of the dynamic talk reach from the front; a static
  // rect keeps their lines reachable where players naturally stand.
  for (const n of GREEN_HARBOR_NPCS) if (n.stall) b.interactions.push({ id: `stall-${n.short}`, ...rectPx(n.stall.front), kind: 'talk', dialogue: n.stall.dialogue, label: n.stall.label });
  observe(b, 'obs-cactus', span(35, 24, 35, 24), 'gh.obs.cactus', '看看仙人球');
  observe(b, 'obs-egg', span(40, 24, 40, 24), 'gh.obs.ostrich-egg', '看看大蛋');
  observe(b, 'obs-pelt', span(41, 23, 42, 23), 'gh.obs.kangaroo-pelt', '看看兽皮');
  observe(b, 'obs-big-leaf', span(24, 27, 31, 27), 'gh.obs.big-leaf', '抬头看大叶子');
  observe(b, 'obs-lantern', span(31, 21, 31, 21), 'gh.obs.lantern', '看看灯笼');
  observe(b, 'obs-hut', span(12, 33, 15, 33), 'gh.obs.hut', '看看吊脚楼');
  // Small turtles: under-body rects on the porch tiles in front of each statue (the elder idles at 51,34, off both).
  observe(b, 'obs-turtle-left', span(48, 34, 49, 34), 'gh.obs.small-turtle', '看看小海龟');
  // The inn seen from afar (first line of the inn paragraph): auto, once, on the approach after the rain beat.
  b.interactions.push({ id: 'obs-inn-far', ...rectPx(GH.innFarView), kind: 'observe', dialogue: 'gh.obs.inn-far', auto: true, once: true, requireFlag: 'RAIN_APPROACHES' });

  // ---------------------------------------------------------------- story hooks
  // Commentary beats are auto triggers; the scene guarantees canonical order even if one is skipped.
  b.interactions.push({ id: 'beat-pier', ...rectPx(GH.beats.pier), kind: 'trigger', cinematic: 'gh.beat.pier', auto: true, once: true, requireFlag: 'HARBOR_FREE_ROAM', untilFlag: 'MERCHANT_TURTLE_EGG' });
  b.interactions.push({ id: 'beat-candle', ...rectPx(GH.beats.candle), kind: 'trigger', cinematic: 'gh.beat.candle', auto: true, once: true, requireFlag: 'HARBOR_FREE_ROAM', untilFlag: 'MERCHANT_TURTLE_EGG' });
  b.interactions.push({ id: 'beat-market', ...rectPx(GH.beats.market), kind: 'trigger', cinematic: 'gh.beat.market', auto: true, once: true, requireFlag: 'HARBOR_FREE_ROAM', untilFlag: 'MERCHANT_TURTLE_EGG' });
  b.interactions.push({ id: 'beat-leaves', ...rectPx(GH.beats.leaves), kind: 'trigger', cinematic: 'gh.beat.leaves', auto: true, once: true, requireFlag: 'HARBOR_FREE_ROAM', untilFlag: 'MERCHANT_TURTLE_EGG' });
  // The 亚北青年: main-line talk until the flag, flavour talk afterwards.
  b.interactions.push({ id: 'talk-merchant', ...rectPx(GH.merchantTalk), kind: 'trigger', cinematic: 'gh.merchant', label: '搭话', requireFlag: 'HARBOR_FREE_ROAM', untilFlag: 'MERCHANT_TURTLE_EGG' });
  b.interactions.push({ id: 'talk-merchant-after', ...rectPx(GH.merchantTalk), kind: 'talk', dialogue: 'gh.npc.yabei-merchant', label: '搭话', requireFlag: 'MERCHANT_TURTLE_EGG' });
  // Inn door before the rain: a look, not an entrance.
  b.interactions.push({ id: 'inn-door-early', ...rectPx(GH.innDoor), kind: 'observe', dialogue: 'gh.obs.inn-door-early', auto: true, untilFlag: 'RAIN_APPROACHES' });

  const npcs: MapNpcPlacement[] = GREEN_HARBOR_NPCS.map((n) => n.placement);

  return {
    id: GH_MAP_ID,
    width: GH_W, height: GH_H, tileSize: 32,
    background: [
      { key: 'env-harbor-sky-far', parallax: 0.2 },
      { key: 'env-harbor-far-shore', parallax: 0.5 },
    ],
    tilesets: [{ id: GH_TILESET_KEY, texture: GH_TILESET_KEY, tileWidth: 32, tileHeight: 32 }],
    foreground: b.foreground.sort((a, c) => (a.depthY ?? a.y) - (c.depthY ?? c.y)),
    layers: [
      { name: 'ground', tileset: GH_TILESET_KEY, data: b.ground, depth: 'ground' },
      { name: 'detail', tileset: GH_TILESET_KEY, data: b.detail, depth: 'detail' },
      { name: 'overhead', tileset: GH_TILESET_KEY, data: b.overhead, depth: 'overhead' },
    ],
    collision: b.collision,
    exits: [
      { id: 'inn-entrance', ...rectPx(GH.innDoor), target: 'turtle-inn', spawn: 'inn-entrance' },
    ],
    spawns: [
      { id: 'pier-start', ...feet(GH.spawns.pierStart.x, GH.spawns.pierStart.y), facing: GH.spawns.pierStart.facing },
      { id: 'market', ...feet(GH.spawns.market.x, GH.spawns.market.y), facing: GH.spawns.market.facing },
      { id: 'inn-approach', ...feet(GH.spawns.innApproach.x, GH.spawns.innApproach.y), facing: GH.spawns.innApproach.facing },
      { id: 'inn-door', ...feet(GH.spawns.innDoor.x, GH.spawns.innDoor.y), facing: GH.spawns.innDoor.facing },
      { id: 'stub', ...feet(GH.spawns.market.x, GH.spawns.market.y), facing: 'right' },
    ],
    interactions: b.interactions,
    npcs,
    lights: b.lights,
    water: b.water.map(rectPx),
    rainShelter: b.shelter.map(rectPx),
    // AudioKeys.music.harbor from @/audio (kept as a literal so this data file stays Phaser-free).
    music: 'music-harbor',
    ambience: ['amb-water-lap', 'amb-market-crowd'],
  };
}

export const GREEN_HARBOR_MAP: MapData = buildGreenHarborMap();

/** Tile-space walkability helper for tests and the scene's fallback pathing. */
export function isTileSolid(map: MapData, tx: number, ty: number): boolean {
  if (tx < 0 || ty < 0 || tx >= map.width || ty >= map.height) return true;
  return (map.collision[ty * map.width + tx] ?? 1) !== 0;
}
